<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-4">
          <h1 class="text-h4 mb-0">{{ $t('transactions.title') }}</h1>
          <v-btn color="primary" @click="openAddDialog">{{ $t('transactions.add') }}</v-btn>
        </div>
      </v-col>
    </v-row>
    
    <!-- Filters -->
    <v-row>
      <v-col cols="12">
        <div class="mb-4">

          <v-row>
            <v-col cols="12" sm="6" md="2" lg="2" xl="2">
              <v-select
                v-model="filterCoin"
                :items="coins"
                item-title="symbol"
                item-value="id"
                :label="$t('transactions.coinFilter')"
                clearable
                density="compact"
              />
            </v-col>
            <v-col cols="12" sm="6" md="2" lg="2" xl="2">
              <v-autocomplete
                v-model="filterWallet"
                v-model:search="walletFilterSearch"
                :items="filteredWalletsForFilter"
                item-title="display_name"
                item-value="id"
                :label="$t('transactions.walletFilter')"
                :placeholder="$t('transactions.walletSearchPlaceholder')"
                clearable
                density="compact"
                auto-select-first
              >
                <template #no-data>
                  <div class="pa-2">{{ $t('transactions.noWalletsFound') }}</div>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" md="2" lg="2" xl="2">
              <v-select
                v-model="filterClient"
                :items="clients"
                item-title="phone"
                item-value="id"
                :label="$t('transactions.clientFilter')"
                clearable
                density="compact"
              />
            </v-col>
            <v-col cols="12" sm="6" md="2" lg="2" xl="2">
              <v-select
                  v-model="filterUser"
                  :items="users"
                  item-title="email"
                  item-value="id"
                  :label="$t('transactions.manager')"
                  clearable
                  density="compact"
              />
            </v-col>
            <v-col cols="12" sm="6" md="2" lg="2" xl="2">
              <v-text-field
                v-model="filterDateFrom"
                :label="$t('transactions.dateFrom')"
                type="date"
                density="compact"
              />
            </v-col>
            <v-col cols="12" sm="6" md="2" lg="2" xl="2">
              <v-text-field
                v-model="filterDateTo"
                :label="$t('transactions.dateTo')"
                type="date"
                density="compact"
              />
            </v-col>
          </v-row>
          <v-row class="period-buttons-row">
            <v-col cols="12" sm="6" md="4" lg="4" xl="4"></v-col>
            <v-col cols="12" sm="4" md="4" lg="4" xl="4">
              <v-btn-toggle v-model="clientType" mandatory class="period-buttons">
                <v-btn value="all" size="small">{{ $t('transactions.all') }}</v-btn>
                <v-btn value="internal" size="small">{{ $t('transactions.internal') }}</v-btn>
                <v-btn value="clients" size="small">{{ $t('transactions.clients') }}</v-btn>
              </v-btn-toggle>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="4" xl="4">
              <v-btn-toggle v-model="datePeriod" mandatory class="period-buttons">
                <v-btn value="day" size="small">{{ $t('transactions.day') }}</v-btn>
                <v-btn value="week" size="small">{{ $t('transactions.week') }}</v-btn>
                <v-btn value="month" size="small">{{ $t('transactions.month') }}</v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-row>
    
    <v-row>
      <v-col cols="12">
        <v-table density="compact">
          <thead>
            <tr>
              <th 
                class="px-4 py-3 sortable-header" 
                :class="{ 'sort-active': sortBy === 'created_at' }"
                style="cursor: pointer; user-select: none;" 
                @click="handleSort('created_at')"
              >
                <div class="d-flex align-center">
                  {{ $t('transactions.dateColumn') }}
                  <v-icon size="small" class="ml-1 sort-icon">{{ getSortIcon('created_at') }}</v-icon>
                </div>
              </th>
              <th 
                class="px-4 py-3 sortable-header" 
                :class="{ 'sort-active': sortBy === 'from_wallet' }"
                style="cursor: pointer; user-select: none;"
                @click="handleSort('from_wallet')"
              >
                <div class="d-flex align-center">
                  {{ $t('transactions.fromWalletColumn') }}
                  <v-icon size="small" class="ml-1 sort-icon">{{ getSortIcon('from_wallet') }}</v-icon>
                </div>
              </th>
              <th 
                class="px-4 py-3 sortable-header" 
                :class="{ 'sort-active': sortBy === 'sold_amount' }"
                style="cursor: pointer; user-select: none;"
                @click="handleSort('sold_amount')"
              >
                <div class="d-flex align-center">
                  {{ $t('transactions.amountColumn') }}
                  <v-icon size="small" class="ml-1 sort-icon">{{ getSortIcon('sold_amount') }}</v-icon>
                </div>
              </th>
              <th 
                class="px-4 py-3 sortable-header" 
                :class="{ 'sort-active': sortBy === 'sold_rate' }"
                style="cursor: pointer; user-select: none;"
                @click="handleSort('sold_rate')"
              >
                <div class="d-flex align-center">
                  {{ $t('transactions.rateFee') }}
                  <v-icon size="small" class="ml-1 sort-icon">{{ getSortIcon('sold_rate') }}</v-icon>
                </div>
              </th>
              <th 
                class="px-4 py-3 sortable-header" 
                :class="{ 'sort-active': sortBy === 'to_wallet' }"
                style="cursor: pointer; user-select: none;"
                @click="handleSort('to_wallet')"
              >
                <div class="d-flex align-center">
                  {{ $t('transactions.toWalletColumn') }}
                  <v-icon size="small" class="ml-1 sort-icon">{{ getSortIcon('to_wallet') }}</v-icon>
                </div>
              </th>
              <th 
                class="px-4 py-3 sortable-header" 
                :class="{ 'sort-active': sortBy === 'received_amount' }"
                style="cursor: pointer; user-select: none;"
                @click="handleSort('received_amount')"
              >
                <div class="d-flex align-center">
                  {{ $t('transactions.amountColumn') }}
                  <v-icon size="small" class="ml-1 sort-icon">{{ getSortIcon('received_amount') }}</v-icon>
                </div>
              </th>
              <th 
                class="px-4 py-3 sortable-header" 
                :class="{ 'sort-active': sortBy === 'received_rate' }"
                style="cursor: pointer; user-select: none;"
                @click="handleSort('received_rate')"
              >
                <div class="d-flex align-center">
                  {{ $t('transactions.rateOurRate') }}
                  <v-icon size="small" class="ml-1 sort-icon">{{ getSortIcon('received_rate') }}</v-icon>
                </div>
              </th>
              <th 
                class="px-4 py-3 sortable-header" 
                :class="{ 'sort-active': sortBy === 'client' }"
                style="cursor: pointer; user-select: none;"
                @click="handleSort('client')"
              >
                <div class="d-flex align-center">
                  {{ $t('transactions.clientOperator') }}
                  <v-icon size="small" class="ml-1 sort-icon">{{ getSortIcon('client') }}</v-icon>
                </div>
              </th>
              <th 
                class="px-4 py-3 sortable-header" 
                :class="{ 'sort-active': sortBy === 'profit' }"
                style="cursor: pointer; user-select: none;"
                @click="handleSort('profit')"
              >
                <div class="d-flex align-center">
                  {{ $t('transactions.profitColumn') }}
                  <v-icon size="small" class="ml-1 sort-icon">{{ getSortIcon('profit') }}</v-icon>
                </div>
              </th>
              <th class="px-4 py-3">{{ $t('transactions.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="transaction in filteredTransactions" :key="transaction.id">
              <!-- Main Row -->
              <tr class="main-row">
                <td>{{ formatTimeForDisplay(transaction.created_at) }}</td>

                <td>
                  {{ transaction.sold_coin_symbol }}
                </td>
                <td class="text-right">{{ transaction.sold_amount }}</td>
                <td class="text-right">{{ formatRate(transaction.sold_rate) }}</td>

                <td>{{ transaction.received_coin_symbol }}</td>
                <td class="text-right">{{ formatRate(transaction.received_amount) }}</td>
                <td class="text-right">{{ formatRate(transaction.received_rate) }}</td>
                <td class="text-right">{{ transaction.client_phone }}</td>
                <td class="text-right">{{ formatProfit(transaction.estimated_profit) }}</td>
                <td class="pa-0 text-center">
                  <v-btn variant="plain" density="compact" size="small" :title="$t('walletOperations.linkTitle')" class="pa-0" icon="mdi-format-list-bulleted" @click="$router.push({ path: '/wallet-operations', query: { document_type: 'Transaction', document_id: transaction.id } })"></v-btn>
                  <v-btn variant="plain" density="compact" size="small" @click="editTransaction(transaction)" icon="mdi-pencil" class="pa-0"></v-btn>
                  <v-btn variant="plain" density="compact" size="small" color="error" @click="deleteTransaction(transaction.id)" class="pa-0" icon="mdi-delete"></v-btn>
                </td>
              </tr>
              <!-- Detail Row -->
              <tr class="detail-row" >
                <td>{{ formatDateForDisplay(transaction.created_at) }}</td>
                <td>                  <router-link v-if="transaction.from_wallet_display" :to="`/wallets/${transaction.from_wallet_id}`">{{ transaction.from_wallet_display }}</router-link>
                  <span v-else>-</span>
</td>
                <td></td>
                <td class="text-right">
                  <div>{{ transaction.send_fee || 0 }}</div>
                </td>
                <td><router-link v-if="transaction.to_wallet_display" :to="`/wallets/${transaction.to_wallet_id}`">{{ transaction.to_wallet_display }}</router-link>
                  <span v-else>-</span></td>
                <td></td>
                <td class="text-right">
                  <div>{{ formatRate(transaction.receive_our_rate) }}</div>
                </td>
                <td class="text-right">{{ transaction.user_email || '-' }}</td>
                <td class="text-right">{{ transaction.id }}</td>
                <td></td>
              </tr>
            </template>
          </tbody>
          <tfoot>
            <tr class="total-row">
              <td colspan="8" class="text-right font-weight-bold">{{ $t('transactions.totalProfit') }}</td>
              <td class="text-right font-weight-bold">{{ formattedTotalProfit }}</td>
              <td></td>
            </tr>
          </tfoot>
        </v-table>
      </v-col>
    </v-row>
    <TransactionEditDialog
      v-model="showAddDialog"
      :transaction="editingTransaction"
      :coins="coins"
      :wallets="wallets"
      :users="users"
      :clients="clients"
      :current-user-id="currentUserId"
      @save="handleSaveTransaction"
      @cancel="handleCancelTransaction"
    />
    <v-snackbar
      v-model="snackbar.visible"
      :color="snackbar.color"
      :timeout="5000"
      location="bottom"
    >
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { apiGet, apiPost, apiPut, apiDelete, getErrorMessage } from '../utils/api'
import { getDateRangeForPeriod, getStartOfDay, getEndOfDay, formatDateForDisplay, formatTimeForDisplay, dateStringToISO, getTodayString, formatDateToYYYYMMDD } from '../utils/date'
import TransactionEditDialog from './dialogs/TransactionEditDialog.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

/** Client ID used for "internal" transactions (no real client) */
const INTERNAL_CLIENT_ID = 1

interface Coin {
  id: number
  name: string
  symbol: string
}

interface Wallet {
  id: number
  exchange: {
    id: number
    name: string
  } | null
  coin: Coin
  display_name?: string
  balance?: number
  calculated_balance?: number
}

interface User {
  id: number
  email: string
}

interface Transaction {
  id: number
  client_id: number
  client_phone: string
  from_wallet_id: number | null
  from_wallet_display: string | null
  sold_coin_id: number
  sold_coin_symbol: string
  sold_amount: number
  sold_rate: number
  to_wallet_id: number | null
  to_wallet_display: string | null
  received_coin_id: number
  received_coin_symbol: string
  received_amount: number
  received_rate: number
  send_fee?: number
  receive_our_rate?: number
  estimated_profit?: number
  user_id?: number | null
  user_email?: string | null
  created_at: string
}

interface Client {
  id: number
  phone: string
}

const transactions = ref<Transaction[]>([])
const clients = ref<Client[]>([])
const coins = ref<Coin[]>([])
const wallets = ref<Wallet[]>([])
const users = ref<User[]>([])
const showAddDialog = ref(false)
const editingTransaction = ref<Transaction | null>(null)
const currentUserId = ref<number | null>(null)

// Filter state
const filterUser = ref<number | null>(null)
const filterCoin = ref<number | null>(null)
const filterWallet = ref<number | null>(null)
const filterClient = ref<number | null>(null)
const filterDateFrom = ref<string | null>(null)
const filterDateTo = ref<string | null>(null)
const datePeriod = ref<string>('day')
const clientType = ref<string>('all') // 'all', 'internal', 'clients'
const userWeekStart = ref<string>('sunday') // 'sunday' or 'monday'
const userTimezone = ref<string>('UTC') // User's timezone preference
const walletFilterSearch = ref('')

// Snackbar for errors (instead of alert)
const snackbar = ref({ visible: false, message: '', color: 'error' as 'error' | 'success' })
function showError(message: string, color: 'error' | 'success' = 'error') {
  snackbar.value = { visible: true, message, color }
}

// Sort state - default to date ascending (oldest first)
const sortBy = ref<string>('created_at')
const sortOrder = ref<'asc' | 'desc'>('asc')
const clientSearch = ref('')
const fromWalletSearch = ref('')
const toWalletSearch = ref('')
const isCalculatingReceivedAmount = ref(false)
const isCalculatingProfit = ref(false)
const isCalculatingReceiveOurRate = ref(false)
const transactionForm = ref({
  id: null as number | null,
  client_id: null as number | null,
  from_wallet_id: null as number | null,
  sold_coin_id: null as number | null,
  sold_amount: 0,
  sold_rate: 0,
  to_wallet_id: null as number | null,
  received_coin_id: null as number | null,
  received_amount: 0,
  received_rate: 0,
  user_id: null as number | null,
  send_fee: 0,
  receive_our_rate: 0,
  estimated_profit: 0,
  transaction_date: null as string | null,
})

const filteredClients = computed(() => {
  if (!clientSearch.value) return clients.value
  const search = clientSearch.value.toLowerCase()
  return clients.value.filter(client =>
    client.phone.toLowerCase().includes(search)
  )
})

/** Filter wallets by search string (display_name, exchange, coin) */
function filterWalletsBySearch(walletList: Wallet[], search: string): Wallet[] {
  if (!walletList?.length) return []
  if (!search?.trim()) return walletList
  const lower = search.toLowerCase()
  return walletList.filter(wallet => {
    const displayName = wallet.display_name || (wallet.exchange?.name ? `${wallet.exchange.name} - ${wallet.coin?.symbol}` : wallet.coin?.symbol) || ''
    return displayName.toLowerCase().includes(lower) ||
      wallet.exchange?.name?.toLowerCase().includes(lower) ||
      wallet.coin?.symbol?.toLowerCase().includes(lower) ||
      wallet.coin?.name?.toLowerCase().includes(lower)
  })
}

const filteredFromWallets = computed(() =>
  filterWalletsBySearch(wallets.value, fromWalletSearch.value)
)
const filteredToWallets = computed(() =>
  filterWalletsBySearch(wallets.value, toWalletSearch.value)
)
const filteredWalletsForFilter = computed(() =>
  filterWalletsBySearch(wallets.value, walletFilterSearch.value)
)

// Computed properties for wallets balances
const fromWalletBalance = computed(() => {
  if (!transactionForm.value.from_wallet_id || !wallets.value || wallets.value.length === 0) {
    return ''
  }
  const wallet = wallets.value.find(w => w.id === transactionForm.value.from_wallet_id)
  if (!wallet) return ''
  const balance = wallet.calculated_balance ?? 0
  return balance.toString()
})

const toWalletBalance = computed(() => {
  if (!transactionForm.value.to_wallet_id || !wallets.value || wallets.value.length === 0) {
    return ''
  }
  const wallet = wallets.value.find(w => w.id === transactionForm.value.to_wallet_id)
  if (!wallet) return ''
  const balance = wallet.calculated_balance ?? 0
  return balance.toString()
})

// Computed properties for USDT values
const soldAmountUsdt = computed(() => {
  const soldAmount = parseFloat(transactionForm.value.sold_amount?.toString() || '0')
  const soldRate = parseFloat(transactionForm.value.sold_rate?.toString() || '0')
  const sendFee = parseFloat(transactionForm.value.send_fee?.toString() || '0')
  if (soldAmount > 0 && soldRate > 0) {
    const usdtValue = (soldAmount - sendFee) * soldRate
    return Math.round(usdtValue * 100000000) / 100000000 // Round to 8 decimal places
  }
  return 0
})

const receivedAmountUsdt = computed(() => {
  const receivedAmount = parseFloat(transactionForm.value.received_amount?.toString() || '0')
  const receivedRate = parseFloat(transactionForm.value.receive_our_rate?.toString() || '0')
  if (receivedAmount > 0 && receivedRate > 0) {
    const usdtValue = receivedAmount * receivedRate
    return Math.round(usdtValue * 100000000) / 100000000 // Round to 8 decimal places
  }
  return 0
})

// Filtered transactions
const filteredTransactions = computed(() => {
  let filtered = transactions.value

  // Filter by user
  if (filterUser.value !== null) {
    filtered = filtered.filter(t => t.user_id === filterUser.value)
  }

  // Filter by coin (sold or received)
  if (filterCoin.value !== null) {
    filtered = filtered.filter(t => 
      t.sold_coin_id === filterCoin.value || t.received_coin_id === filterCoin.value
    )
  }

  // Filter by wallets (from or to)
  if (filterWallet.value !== null) {
    filtered = filtered.filter(t => 
      t.from_wallet_id === filterWallet.value || t.to_wallet_id === filterWallet.value
    )
  }

  // Filter by client type (internal, clients, or all)
  // This filter takes precedence over the specific client filter
  if (clientType.value === 'internal') {
    filtered = filtered.filter(t => t.client_id === INTERNAL_CLIENT_ID)
  } else if (clientType.value === 'clients') {
    filtered = filtered.filter(t => t.client_id !== INTERNAL_CLIENT_ID)
  }
  // 'all' shows everything, but still respect specific client filter if set

  // Filter by specific client (only if clientType is 'all')
  if (clientType.value === 'all' && filterClient.value !== null) {
    filtered = filtered.filter(t => t.client_id === filterClient.value)
  }

  // Filter by date range
  if (filterDateFrom.value) {
    const fromDate = getStartOfDay(filterDateFrom.value)
    filtered = filtered.filter(t => {
      const transactionDate = getStartOfDay(t.created_at)
      return transactionDate >= fromDate
    })
  }

  if (filterDateTo.value) {
    const toDate = getEndOfDay(filterDateTo.value)
    filtered = filtered.filter(t => {
      const transactionDate = new Date(t.created_at)
      return transactionDate <= toDate
    })
  }

  // Sort transactions using getSortValue for field mapping
  const sorted = [...filtered].sort((a, b) => {
    const aValue = getSortValue(a, sortBy.value)
    const bValue = getSortValue(b, sortBy.value)
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortOrder.value === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue)
    }
    const cmp = aValue > bValue ? 1 : aValue < bValue ? -1 : 0
    return sortOrder.value === 'asc' ? cmp : -cmp
  })
  return sorted
})

/** Get value for sorting by column (used in filteredTransactions sort) */
function getSortValue(t: Transaction, column: string): string | number {
  const map: Record<string, string | number> = {
    created_at: new Date(t.created_at).getTime(),
    from_wallet: t.from_wallet_display || '',
    sold_amount: t.sold_amount || 0,
    sold_rate: t.sold_rate || 0,
    to_wallet: t.to_wallet_display || '',
    received_amount: t.received_amount || 0,
    received_rate: t.received_rate || 0,
    client: t.client_phone || '',
    profit: t.estimated_profit || 0,
  }
  return map[column] ?? 0
}

// Format profit to 2 decimal places
const formatProfit = (profit: number | null | undefined): string => {
  const value = typeof profit === 'number' ? profit : parseFloat(String(profit || 0))
  if (isNaN(value)) return '0.00'
  return value.toFixed(2)
}

// Format rate to match decimal places (up to 8 decimal places, remove trailing zeros)
// If digits before decimal point > 1, show only 2 decimal places
const formatRate = (rate: number | null | undefined): string => {
  const value = typeof rate === 'number' ? rate : parseFloat(String(rate || 0))
  if (isNaN(value)) return '0'
  
  // Check if integer part has more than 1 digit
  const integerPart = Math.floor(Math.abs(value))
  const hasMoreThanOneDigit = integerPart >= 10
  
  if (hasMoreThanOneDigit) {
    // Show only 2 decimal places for numbers >= 10
    return value.toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })
  } else {
    // Show up to 8 decimal places for numbers < 10
    return value.toLocaleString('en-US', { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 8 
    })
  }
}

// Calculate total profit from filtered transactions
const totalProfit = computed((): number => {
  return filteredTransactions.value.reduce((sum, transaction) => {
    return sum + (parseFloat(transaction.estimated_profit?.toString() || '0') || 0)
  }, 0)
})

const formattedTotalProfit = computed(() => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(totalProfit.value)
})

// Sort handler
const handleSort = (column: string) => {
  if (sortBy.value === column) {
    // Toggle sort order if clicking the same column
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    // Set new column and default to ascending
    sortBy.value = column
    sortOrder.value = 'asc'
  }
}

// Get sort icon for column
const getSortIcon = (column: string) => {
  if (sortBy.value !== column) return 'mdi-sort'
  return sortOrder.value === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending'
}

// Set date period
const setDatePeriod = (period: string) => {
  const dateRange = getDateRangeForPeriod(period, userWeekStart.value)
  filterDateFrom.value = dateRange.from
  filterDateTo.value = dateRange.to
}

// Watch date period changes
watch(datePeriod, (newPeriod) => {
  setDatePeriod(newPeriod)
})

// Watch user week start preference changes and recalculate if on week period
watch(userWeekStart, () => {
  if (datePeriod.value === 'week') {
    setDatePeriod('week')
  }
})

// Listen for preference changes from Settings page (clean up on unmount)
const onWeekStartChanged = (e: Event) => {
  const detail = (e as CustomEvent).detail
  if (detail?.weekStart) userWeekStart.value = detail.weekStart
}
const onTimezoneChanged = (e: Event) => {
  const detail = (e as CustomEvent).detail
  if (detail?.timezone) userTimezone.value = detail.timezone
}

// Watch client type changes and clear specific client filter
watch(clientType, (newType) => {
  if (newType !== 'all') {
    filterClient.value = null
  }
})

// URL sync: read filters from route query
function initFromQuery() {
  const q = route.query
  const coinId = q.coin != null ? parseInt(String(q.coin), 10) : NaN
  const walletId = q.wallet != null ? parseInt(String(q.wallet), 10) : NaN
  const clientId = q.client != null ? parseInt(String(q.client), 10) : NaN
  const userId = q.user != null ? parseInt(String(q.user), 10) : NaN
  filterCoin.value = Number.isFinite(coinId) ? coinId : null
  filterWallet.value = Number.isFinite(walletId) ? walletId : null
  filterClient.value = Number.isFinite(clientId) ? clientId : null
  filterUser.value = Number.isFinite(userId) ? userId : null
  filterDateFrom.value = (q.date_from as string) || null
  filterDateTo.value = (q.date_to as string) || null
  const period = (q.period as string) || 'day'
  if (['day', 'week', 'month'].includes(period)) {
    datePeriod.value = period
  }
  const cType = (q.client_type as string) || 'all'
  if (['all', 'internal', 'clients'].includes(cType)) {
    clientType.value = cType
  }
  const sortCol = (q.sort_by as string) || 'created_at'
  sortBy.value = sortCol
  const order = (q.sort_order as string) || 'asc'
  if (order === 'asc' || order === 'desc') {
    sortOrder.value = order
  }
  // Apply period to dates if we came from URL without explicit dates
  if (!q.date_from && !q.date_to) {
    setDatePeriod(datePeriod.value)
  }
}

// URL sync: write current filters to route query
function syncFiltersToUrl() {
  const query: Record<string, string> = {}
  if (filterCoin.value != null) query.coin = String(filterCoin.value)
  if (filterWallet.value != null) query.wallet = String(filterWallet.value)
  if (filterClient.value != null) query.client = String(filterClient.value)
  if (filterUser.value != null) query.user = String(filterUser.value)
  if (filterDateFrom.value) query.date_from = filterDateFrom.value
  if (filterDateTo.value) query.date_to = filterDateTo.value
  query.period = datePeriod.value
  if (clientType.value !== 'all') query.client_type = clientType.value
  query.sort_by = sortBy.value
  query.sort_order = sortOrder.value
  router.replace({ path: route.path, query })
}

const digitsOnly = (str: string) => str.replace(/\D/g, '')

const shouldShowSaveClientButton = computed(() => {
  const digits = digitsOnly(clientSearch.value)
  if (digits.length < 10) return false
  
  // If no client is selected, show button
  if (!transactionForm.value.client_id) return true
  
  // If client is selected, check if search value matches current client's phone
  const currentClient = clients.value.find(c => c.id === transactionForm.value.client_id)
  const currentPhoneDigits = currentClient ? digitsOnly(currentClient.phone) : ''
  const searchDigits = digitsOnly(clientSearch.value)
  
  // Show button if search doesn't match current client (allows creating new client when editing)
  return searchDigits !== currentPhoneDigits
})

const saveClientFromSearch = async () => {
  const phone = clientSearch.value.trim()
  if (!phone || digitsOnly(phone).length < 10) {
    showError(t('transactions.enterValidPhone'))
    return
  }

  try {
    const response = await apiPost('/api/clients', { client: { phone: phone } })

    if (response.ok) {
      const newClient = await response.json()
      // Clear the old client_id and set the new one
      transactionForm.value.client_id = newClient.id
      await loadClients()
      // Update search to show the new client's phone
      clientSearch.value = phone
      // Force the autocomplete to recognize the new client
      await new Promise(resolve => setTimeout(resolve, 100))
    } else {
      const errors = await getErrorMessage(response, t('errors.unknownError'))
      showError(t('transactions.failedToCreateClient', { errors }))
    }
  } catch (error) {
    console.error('Save client error:', error)
    // If it's a 401 error, the api utility will handle redirect
    if (error instanceof Error && error.message.includes('Unauthorized')) {
      return // Don't show alert, redirect is happening
    }
    showError(t('transactions.errorCreatingClient'))
  }
}

const loadTransactions = async () => {
  try {
    const response = await apiGet('/api/transactions')
    if (response.ok) {
      transactions.value = await response.json()
    }
  } catch (error) {
    console.error('Load transactions error:', error)
  }
}

const loadClients = async () => {
  try {
    const response = await apiGet('/api/clients')
    if (response.ok) {
      clients.value = await response.json()
    }
  } catch (error) {
    console.error('Load clients error:', error)
  }
}

const loadWallets = async () => {
  try {
    const response = await apiGet('/api/wallets')
    if (response.ok) {
      const walletData = await response.json()
      wallets.value = walletData.map((w: any) => {
        const exchangeName = w.exchange?.name || ''
        const coinSymbol = w.coin?.symbol || ''
        const displayName = exchangeName ? `${exchangeName} - ${coinSymbol}` : coinSymbol
        return {
        ...w,
          display_name: displayName,
          // Ensure all required fields are present
          id: w.id,
          exchange: w.exchange || null,
          coin: w.coin || { id: 0, name: '', symbol: '' }
        }
      })
    } else {
      console.error('Failed to load wallets:', response.status)
    }
  } catch (error) {
    console.error('Load wallets error:', error)
  }
}

const loadCurrentUser = async () => {
  try {
    const response = await apiGet('/api/users/current')
    if (response.ok) {
      const userData = await response.json()
      userWeekStart.value = userData.week_start || 'sunday'
      userTimezone.value = userData.timezone || 'UTC'
      currentUserId.value = userData.id
      return userData.id
    }
  } catch (error) {
    console.error('Load current user error:', error)
  }
  return null
}

const loadCoins = async () => {
  try {
    const response = await apiGet('/api/coins')
    if (response.ok) {
      coins.value = await response.json()
    }
  } catch (error) {
    console.error('Load coins error:', error)
  }
}

const loadUsers = async () => {
  try {
    const response = await apiGet('/api/users')
    if (response.ok) {
      users.value = await response.json()
    }
  } catch (error) {
    console.error('Load users error:', error)
  }
}

// Ensure wallets are loaded when dialog opens
const ensureWalletsLoaded = async () => {
  if (!wallets.value || wallets.value.length === 0) {
    await loadWallets()
  }
}

// Fetch rate from database for a coin.
// If created_at is provided, fetches the latest rate before or equal to that date.
// symbol: when 'USDT'/'USDC' and no rate in DB, returns 1 so BTC->USDT received_amount is correct.
const fetchRateFromDB = async (coinId: number | null, created_at?: string | null, symbol?: string | null) => {
  if (!coinId) return null
  
  try {
    let url = `/api/coin_rates/latest?coin_id=${coinId}`
    if (created_at) {
      url += `&date_before=${created_at}`
    }
    const response = await apiGet(url)
    if (response.ok) {
      const rateData = await response.json()
      const rate = rateData.rate != null && Number(rateData.rate) > 0 ? Number(rateData.rate) : null
      if (rate == null && (symbol?.toUpperCase() === 'USDT' || symbol?.toUpperCase() === 'USDC')) {
        return 1
      }
      return rate
    }
  } catch (error) {
    console.error('Fetch rate from DB error:', error)
  }
  if (symbol?.toUpperCase() === 'USDT' || symbol?.toUpperCase() === 'USDC') {
    return 1
  }
  return null
}

// Watch for wallets changes and automatically set corresponding coin
watch(() => transactionForm.value.from_wallet_id, async (newWalletId) => {
  if (!newWalletId) return
  
  // Ensure wallets are loaded
  if (!wallets.value || wallets.value.length === 0) {
    await ensureWalletsLoaded()
  }
  
  if (wallets.value && wallets.value.length > 0) {
    const wallet = wallets.value.find(w => w.id === newWalletId)
    if (wallet && wallet.coin && wallet.coin.id) {
      transactionForm.value.sold_coin_id = wallet.coin.id
      // Fetch rate from database using transaction_date if available
      const created_at = transactionForm.value.transaction_date 
        ? dateStringToISO(transactionForm.value.transaction_date)
        : null
      const rate = await fetchRateFromDB(wallet.coin.id, created_at)
      if (rate) {
        transactionForm.value.sold_rate = rate
      }
    }
  }
})

watch(() => transactionForm.value.to_wallet_id, async (newWalletId) => {
  if (!newWalletId) return
  
  // Ensure wallets are loaded
  if (!wallets.value || wallets.value.length === 0) {
    await ensureWalletsLoaded()
  }
  
  if (wallets.value && wallets.value.length > 0) {
    const wallet = wallets.value.find(w => w.id === newWalletId)
    if (wallet && wallet.coin && wallet.coin.id) {
      transactionForm.value.received_coin_id = wallet.coin.id
      const created_at = transactionForm.value.transaction_date 
        ? dateStringToISO(transactionForm.value.transaction_date)
        : null
      const symbol = wallet.coin.symbol ?? null
      const rate = await fetchRateFromDB(wallet.coin.id, created_at, symbol)
      if (rate != null) {
        transactionForm.value.received_rate = rate
      }
    }
  }
})

// Watch for coin changes and fetch rates from database
watch(() => transactionForm.value.sold_coin_id, async (newCoinId) => {
  if (newCoinId) {
    // Fetch rate from database using transaction_date if available
    const created_at = transactionForm.value.transaction_date 
      ? new Date(transactionForm.value.transaction_date).toISOString()
      : null
    const rate = await fetchRateFromDB(newCoinId, created_at)
    if (rate) {
      transactionForm.value.sold_rate = rate
    }
    // Calculate receive_our_rate after rates are updated and received_amount is calculated
    setTimeout(() => {
      calculateReceiveOurRate()
    }, 100)
  }
})

watch(() => transactionForm.value.received_coin_id, async (newCoinId) => {
  if (newCoinId) {
    const symbol = coins.value?.find((c: Coin) => c.id === newCoinId)?.symbol ?? null
    const created_at = transactionForm.value.transaction_date 
      ? new Date(transactionForm.value.transaction_date).toISOString()
      : null
    const rate = await fetchRateFromDB(newCoinId, created_at, symbol)
    if (rate != null) {
      transactionForm.value.received_rate = rate
    }
    setTimeout(() => {
      calculateReceiveOurRate()
    }, 100)
  }
})

// Watch for transaction_date changes and refetch rates
watch(() => transactionForm.value.transaction_date, async (newDate) => {
  // Refetch rates when date changes if coins are already selected
  if (newDate && transactionForm.value.sold_coin_id) {
    const created_at = new Date(newDate).toISOString()
    const rate = await fetchRateFromDB(transactionForm.value.sold_coin_id, created_at)
    if (rate) {
      transactionForm.value.sold_rate = rate
    }
  }
  if (newDate && transactionForm.value.received_coin_id) {
    const created_at = new Date(newDate).toISOString()
    const symbol = coins.value?.find((c: Coin) => c.id === transactionForm.value.received_coin_id)?.symbol ?? null
    const rate = await fetchRateFromDB(transactionForm.value.received_coin_id, created_at, symbol)
    if (rate != null) {
      transactionForm.value.received_rate = rate
    }
  }
})

// Calculate received amount when sold amount, sold rate, or received rate changes
// Always use received_rate (market rate) for this calculation, not receive_our_rate
const calculateReceivedAmount = () => {
  if (isCalculatingReceivedAmount.value) return
  
  const soldAmount = parseFloat(transactionForm.value.sold_amount?.toString() || '0')
  const soldRate = parseFloat(transactionForm.value.sold_rate?.toString() || '0')
  let receivedRate = parseFloat(transactionForm.value.received_rate?.toString() || '0')
  // USDT/USDC often have no rate in DB - use 1 so BTC->USDT received_amount is correct
  if (receivedRate <= 0 && transactionForm.value.received_coin_id && coins.value?.length) {
    const coin = coins.value.find((c: Coin) => c.id === transactionForm.value.received_coin_id)
    if (coin?.symbol?.toUpperCase() === 'USDT' || coin?.symbol?.toUpperCase() === 'USDC') {
      receivedRate = 1
    }
  }
  
  if (soldAmount > 0 && soldRate > 0 && receivedRate > 0) {
    isCalculatingReceivedAmount.value = true
    const calculatedAmount = (soldAmount * soldRate) / receivedRate
    transactionForm.value.received_amount = Math.round(calculatedAmount * 100000000) / 100000000 // Round to 8 decimal places
    setTimeout(() => {
      isCalculatingReceivedAmount.value = false
    }, 0)
  } else if (soldAmount === 0 || soldRate === 0 || receivedRate === 0) {
    transactionForm.value.received_amount = 0
  }
}

// Calculate estimated profit when relevant fields change
const calculateEstimatedProfit = () => {
  if (isCalculatingProfit.value) return
  
  const soldAmount = parseFloat(transactionForm.value.sold_amount?.toString() || '0')
  const soldRate = parseFloat(transactionForm.value.sold_rate?.toString() || '0')
  const receivedAmount = parseFloat(transactionForm.value.received_amount?.toString() || '0')
  // Use receive_our_rate if available, otherwise fall back to received_rate
  const receivedRate = parseFloat(transactionForm.value.receive_our_rate?.toString() || transactionForm.value.received_rate?.toString() || '0')
  const sendFee = parseFloat(transactionForm.value.send_fee?.toString() || '0')
  
  if (soldAmount > 0 && soldRate > 0 && receivedAmount > 0 && receivedRate > 0) {
    isCalculatingProfit.value = true
    const soldValue = soldAmount * soldRate
    const receivedValue = receivedAmount * receivedRate
    const profit = receivedValue - soldValue - sendFee * soldRate
    transactionForm.value.estimated_profit = Math.round(profit * 100000000) / 100000000 // Round to 8 decimal places
    setTimeout(() => {
      isCalculatingProfit.value = false
    }, 0)
  } else {
    // Clear profit if any required value is zero
    transactionForm.value.estimated_profit = 0
  }
}

watch(() => transactionForm.value.sold_amount, () => {
  calculateReceivedAmount()
  calculateEstimatedProfit()
  // Calculate receive_our_rate after received_amount is calculated
  setTimeout(() => {
    calculateReceiveOurRate()
  }, 0)
})

watch(() => transactionForm.value.sold_rate, () => {
  calculateReceivedAmount()
  calculateEstimatedProfit()
  // Calculate receive_our_rate after received_amount is calculated
  setTimeout(() => {
    calculateReceiveOurRate()
  }, 0)
})

watch(() => transactionForm.value.received_amount, () => {
  calculateEstimatedProfit()
  calculateReceiveOurRate()
})

watch(() => transactionForm.value.received_rate, () => {
  calculateReceivedAmount()
  calculateEstimatedProfit()
  // Calculate receive_our_rate after received_amount is calculated
  setTimeout(() => {
    calculateReceiveOurRate()
  }, 0)
})

// Calculate receive_our_rate when sold amount, sold coin, or received coin changes
const calculateReceiveOurRate = () => {
  if (isCalculatingReceiveOurRate.value) return
  
  const soldAmount = parseFloat(transactionForm.value.sold_amount?.toString() || '0')
  const soldRate = parseFloat(transactionForm.value.sold_rate?.toString() || '0')
  const receivedAmount = parseFloat(transactionForm.value.received_amount?.toString() || '0')
  
  if (soldAmount > 0 && soldRate > 0 && receivedAmount > 0) {
    isCalculatingReceiveOurRate.value = true
    const calculatedRate = (soldAmount * soldRate) / receivedAmount
    transactionForm.value.receive_our_rate = Math.round(calculatedRate * 100000000) / 100000000 // Round to 8 decimal places
    setTimeout(() => {
      isCalculatingReceiveOurRate.value = false
    }, 0)
  } else if (soldAmount === 0 || soldRate === 0 || receivedAmount === 0) {
    // Clear receive_our_rate if any required value is zero
    transactionForm.value.receive_our_rate = 0
  }
}

watch(() => transactionForm.value.receive_our_rate, () => {
  calculateReceivedAmount()
  calculateEstimatedProfit()
})

watch(() => transactionForm.value.send_fee, () => {
  calculateEstimatedProfit()
})

const handleSaveTransaction = async (formData: any) => {
  try {
    if (!formData.client_id) {
      showError(t('transactions.selectClientOrPhone'))
      return
    }
    
    const url = formData.id ? `/api/transactions/${formData.id}` : '/api/transactions'
    // Prepare transaction data, converting transaction_date to created_at if provided
    // Remove id from transactionData as it's already in the URL for updates
    const { id, ...formDataWithoutId } = formData
    const transactionData: any = { 
      ...formDataWithoutId, 
      client_id: formData.client_id,
      sold_coin_id: formData.sold_coin_id,
      received_coin_id: formData.received_coin_id,
      from_wallet_id: formData.from_wallet_id,
      to_wallet_id: formData.to_wallet_id
    }
    // If transaction_date is provided, convert it to created_at format
    if (transactionData.transaction_date) {
      transactionData.created_at = dateStringToISO(transactionData.transaction_date) || new Date().toISOString()
      delete transactionData.transaction_date
    }
    const body = { transaction: transactionData }
    
    const response = formData.id 
      ? await apiPut(url, body)
      : await apiPost(url, body)
    
    if (response.ok) {
      showAddDialog.value = false
      editingTransaction.value = null
      await loadTransactions()
      await loadClients() // Reload clients in case a new one was created
    } else {
      const errors = await getErrorMessage(response, t('errors.unknownError'))
      showError(t('transactions.errorMessage', { errors }))
    }
  } catch (error) {
    console.error('Save transaction error:', error)
    showError(t('transactions.errorSaving'))
  }
}

const handleCancelTransaction = () => {
  editingTransaction.value = null
}

/** Reset form to default for new transaction. Called when opening "Add" dialog. */
const resetForm = async () => {
  const userId = currentUserId.value ?? await loadCurrentUser()
  transactionForm.value = {
    id: null,
    client_id: null,
    from_wallet_id: null,
    sold_coin_id: null,
    sold_amount: 0,
    sold_rate: 0,
    to_wallet_id: null,
    received_coin_id: null,
    received_amount: 0,
    received_rate: 0,
    user_id: userId,
    send_fee: 0,
    receive_our_rate: 0,
    estimated_profit: 0,
    transaction_date: getTodayString(),
  }
  clientSearch.value = ''
  fromWalletSearch.value = ''
  toWalletSearch.value = ''
}

const editTransaction = async (transaction: Transaction) => {
  editingTransaction.value = transaction
  showAddDialog.value = true
}

const openAddDialog = async () => {
  editingTransaction.value = null
  await resetForm()
  showAddDialog.value = true
}

const deleteTransaction = async (id: number) => {
  if (!confirm(t('transactions.areYouSure'))) return
  try {
    const response = await apiDelete(`/api/transactions/${id}`)
    if (response.ok) {
      await loadTransactions()
    }
  } catch (error) {
    console.error('Delete transaction error:', error)
  }
}

// Sync filters to URL when user changes them
watch([filterCoin, filterWallet, filterClient, filterUser, filterDateFrom, filterDateTo, datePeriod, clientType, sortBy, sortOrder], () => {
  syncFiltersToUrl()
}, { deep: true })

// Restore filters from URL on route change (e.g. browser back/forward)
watch(() => route.query, () => {
  initFromQuery()
}, { deep: true })

onMounted(async () => {
  window.addEventListener('week-start-changed', onWeekStartChanged)
  window.addEventListener('timezone-changed', onTimezoneChanged)
  await loadCurrentUser() // Load user preferences first (needed for week period)
  initFromQuery()         // Restore filters from URL or set defaults
  syncFiltersToUrl()      // Ensure URL reflects current state
  await Promise.all([
    loadTransactions(),
    loadClients(),
    loadCoins(),
    loadWallets(),
    loadUsers(),
  ])
})

onUnmounted(() => {
  window.removeEventListener('week-start-changed', onWeekStartChanged)
  window.removeEventListener('timezone-changed', onTimezoneChanged)
})
</script>

<style scoped>

/* Sort icon visibility - hide by default, show on hover or when active */
.sortable-header .sort-icon {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.sortable-header:hover .sort-icon,
.sortable-header.sort-active .sort-icon {
  opacity: 1;
}

.main-row td {
  border-bottom: none !important;
}

.detail-row {
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-size: 0.6rem;
}


.total-row {
  background-color: rgba(var(--v-theme-on-surface), 0.05);
  font-weight: bold;
  border-top: 2px solid rgba(var(--v-theme-on-surface), 0.12) !important;
}

.period-buttons-row {
  margin-top: -38px;
}

.period-buttons :deep(.v-btn) {
  margin: 0;
  padding: 4px 12px;
  min-width: auto;
  height: 24px;
  max-height: 24px;
}

.period-buttons :deep(.v-btn-toggle__content) {
  gap: 0;
}

/* Optimize for very wide screens (1920px and above) */
@media (min-width: 1920px) {
  .v-container {
    max-width: 1800px;
  }
}

/* Optimize for ultra-wide screens (2560px and above) */
@media (min-width: 2560px) {
  .v-container {
    max-width: 2400px;
  }
}
</style>

